import React from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ColorScheme, Flex } from '@hivewatch/dark'

import {
  ControlledSelect,
  ControlledTextInput,
  FacilityOptionLabel,
} from '@/components/ui'
import { DeviceType } from '@/graphql/generated/schemas'
import { createFacilityAddressFilter } from '@/utils/forms/optionBuilders'
import {
  getFacilityNoOptionsMessage,
  maxLength,
} from '@/utils/forms/validation'

import { Options } from '../../types/types'
import { FormInputsIProps } from '../types/types'

interface DarkGenericDeviceFormIProps {
  form: UseFormMethods<FormInputsIProps>
  facilityOptions: Options
  floorOptions: Options
  isOptionsLoading: boolean
  isFacilitiesLoading: boolean
  isFacilitiesError: boolean
  isIncidentTypesLoading: boolean
  incidentTypeOptions: Options
  setFacilityName?: React.Dispatch<React.SetStateAction<string>>
  colorScheme: ColorScheme
}

/**
 * Rebrand variant of {@link GenericDeviceForm}. Same fields, same validation,
 * and the alarm Incident Type field stays behind `useEventPromotionAlarms`.
 */
export const DarkGenericDeviceForm = ({
  form,
  isOptionsLoading,
  isFacilitiesLoading,
  isFacilitiesError,
  facilityOptions,
  floorOptions,
  isIncidentTypesLoading,
  incidentTypeOptions,
  setFacilityName,
  colorScheme,
}: DarkGenericDeviceFormIProps) => {
  const { t } = useTranslation()
  const { control, errors, watch } = form
  const isEventPromotion = useFeatureFlag(FeatureFlag.useEventPromotionAlarms)
  const type = watch('type')

  return (
    <Flex direction='column' gap='16px'>
      <ControlledTextInput
        colorScheme={colorScheme}
        control={control}
        data-testid='devicesPage_genericModal_name'
        errors={errors}
        label={t('common:device_name')}
        name='name'
        placeholder={t('devices:crud_device_name_placeholder')}
        rules={{
          maxLength: maxLength(255),
          required: t('devices:crud_device_name_required'),
        }}
        width='100%'
      />

      <ControlledSelect
        colorScheme={colorScheme}
        control={control}
        data-testid='devicesPage_genericModal_facility'
        filterOption={createFacilityAddressFilter}
        formatOptionLabel={(option, meta) => (
          <FacilityOptionLabel
            colorScheme={colorScheme}
            isValueContext={meta?.context === 'value'}
            option={option}
          />
        )}
        hasWarning={!!errors?.facility}
        isClearable
        isDisabled={isOptionsLoading}
        isLoading={isFacilitiesLoading}
        label={t('common:facility')}
        name='facility'
        noOptionsMessage={({ inputValue }) =>
          getFacilityNoOptionsMessage(
            inputValue,
            isFacilitiesError,
            t('common:facility_search_error')
          )
        }
        onInputChange={setFacilityName}
        options={facilityOptions}
        placeholder={t('devices:crud_facility_select')}
        rules={{ required: true }}
        warningText={t('devices:crud_facility_required')}
      />

      <ControlledSelect
        colorScheme={colorScheme}
        control={control}
        data-testid='devicesPage_genericModal_floor'
        hasWarning={!!errors?.floor}
        isClearable
        isDisabled={isOptionsLoading || floorOptions.length === 0}
        label={t('common:floor')}
        name='floor'
        options={floorOptions}
        placeholder={t('devices:crud_floor_select')}
        rules={{ required: true }}
        warningText={t('devices:crud_floor_required')}
      />

      {isEventPromotion && type?.value === DeviceType.Alarm && (
        <ControlledSelect
          colorScheme={colorScheme}
          control={control}
          data-testid='devicesPage_genericModal_alarmTypeId'
          isClearable
          isDisabled={
            isIncidentTypesLoading || incidentTypeOptions.length === 0
          }
          isLoading={isIncidentTypesLoading}
          label={t('common:incident_type')}
          name='alarmTypeId'
          options={incidentTypeOptions}
          placeholder={t('devices:crud_incident_type_placeholder')}
        />
      )}
    </Flex>
  )
}
